<template>
  <div class="kepler-laws-combined-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$T² / a³ = k | dA/dt = const$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="280" viewBox="0 0 400 280">
          <defs>
             <radialGradient id="sunGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stop-color="#fbbf24" />
                <stop offset="100%" stop-color="rgba(251, 191, 36, 0)" />
             </radialGradient>
             <filter id="planet-glow"><feGaussianBlur stdDeviation="2" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- Starry Background -->
          <circle v-for="s in stars" :key="s.id" :cx="s.x" :cy="s.y" :r="s.r" fill="#fff" :opacity="s.o" />

          <!-- The Sun (at one focus) -->
          <g :transform="`translate(${focusX}, 140)`">
             <circle r="15" fill="url(#sunGlow)" />
             <circle r="8" fill="#fbbf24" />
             <text y="25" fill="#facc15" font-size="10" font-weight="900" text-anchor="middle">焦点 F1 (太阳)</text>
          </g>

          <ellipse cx="200" cy="140" :rx="a" :ry="b" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1.5" />

          <!-- Equal Area Sectors (Sweep Area Visualization) -->
          <path v-for="s in sweepSectors" :key="s.id" :d="s.path" fill="rgba(56, 189, 248, 0.2)" stroke="rgba(56, 189, 248, 0.4)" />

          <!-- Current Sweeping Area -->
          <path :d="currentSweepPath" fill="rgba(56, 189, 248, 0.4)" stroke="#38bdf8" stroke-width="1" />

          <!-- The Planet -->
          <g :transform="`translate(${planetX}, ${planetY})`">
             <circle r="6" fill="#38bdf8" filter="url(#planet-glow)" />
             <!-- Velocity Vector -->
             <line x1="0" y1="0" :x2="vVec.x" :y2="vVec.y" stroke="#ef4444" stroke-width="2" marker-end="url(#kh-arr-red)" />
             <text :x="vVec.x + 10" :y="vVec.y" fill="#ef4444" font-size="10" font-weight="bold">v</text>
          </g>

          <defs>
              <marker id="kh-arr-red" markerWidth="6" markerHeight="4" refX="5" refY="2" orient="auto">
                 <path d="M0,0 L6,2 L0,4 Z" fill="#ef4444" />
              </marker>
          </defs>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>离心率 e</label>
              <span class="val">{{ eccentricity.toFixed(2) }}</span>
           </div>
           <div class="hud-item orange">
              <label>实时速速率</label>
              <span class="val">{{ planetSpeed.toFixed(1) }} <small>km/s</small></span>
           </div>
           <div class="hud-item green">
              <label>扫过面积 dA/dt</label>
              <span class="val">恒定 50.0</span>
           </div>
        </div>

        <div class="law-tag">{{ currentLaw }}</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>轨道离心率 e (0为正圆)</label>
                 <el-slider v-model="eccentricity" :min="0" :max="0.8" :step="0.01" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>轨道半长轴 a</label>
                 <el-slider v-model="a" :min="100" :max="180" size="small"></el-slider>
              </div>
              <div class="s-item flex flex-row items-center justify-center" style="grid-column: span 2;">
                 <el-button-group>
                    <el-button type="primary" size="mini" @click="running = !running">{{ running ? '暂停主轴' : '启动模拟' }}</el-button>
                    <el-button type="success" size="mini" @click="saveSector">记录固定时段面积</el-button>
                    <el-button size="mini" @click="clearSectors">清空纪录</el-button>
                 </el-button-group>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>开普勒定律集锦：</strong>[1] <strong>轨道定律</strong>：行星绕太阳的轨道是椭圆，太阳处在椭圆的一个焦点上；[2] <strong>面积定律</strong>：太阳到行星的连线在相等时间内扫过<strong>相等的面积</strong>。这导致行星在离太阳近（近日点）时跑得快，远时慢；[3] <strong>周期定律</strong>：轨道公转周期的平方与半长轴的三次方成正比。通过调节离心率，你可以直观感知近日点速度剧增的物理美感。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'KeplerLawsViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      eccentricity: 0.5,
      a: 140,
      angle: 0,
      running: true,
      sweepSectors: [],
      stars: Array.from({length: 30}, (_, i) => ({ id:i, x: Math.random()*400, y: Math.random()*280, r: Math.random()*1, o: Math.random()*0.5 + 0.5 })),
      timer: null
    }
  },
  computed: {
    b() { return this.a * Math.sqrt(1 - this.eccentricity * this.eccentricity); },
    focusX() { return 200 - this.a * this.eccentricity; },
    planetX() { return 200 + this.a * Math.cos(this.angle); },
    planetY() { return 140 + this.b * Math.sin(this.angle); },
    planetSpeed() {
       const dist = Math.sqrt(Math.pow(this.planetX - this.focusX, 2) + Math.pow(this.planetY - 140, 2));
       // h = r^2 * d(angle)/dt.  Let h = constant
       return 500 / dist; 
    },
    currentSweepPath() {
       let prevAngle = this.angle - 0.15;
       let x1 = 200 + this.a * Math.cos(prevAngle);
       let y1 = 140 + this.b * Math.sin(prevAngle);
       return `M ${this.focusX} 140 L ${x1} ${y1} A ${this.a} ${this.b} 0 0 1 ${this.planetX} ${this.planetY} Z`;
    },
    vVec() {
       // Vector orthogonal to the radius isn't correct for ellipse, should be tangent
       let dx = -this.a * Math.sin(this.angle);
       let dy = this.b * Math.cos(this.angle);
       let mag = Math.sqrt(dx*dx + dy*dy);
       let s = this.planetSpeed * 5; 
       return { x: (dx/mag) * s, y: (dy/mag) * s };
    },
    currentLaw() {
       const dist = Math.sqrt(Math.pow(this.planetX - this.focusX, 2) + Math.pow(this.planetY - 140, 2));
       if (dist < this.a * 0.8) return '第二定律：近日点速度增加';
       if (dist > this.a * 1.2) return '第二定律：远日点速度减缓';
       return '第一定律：椭圆轨道运行';
    }
  },
  methods: {
    saveSector() {
       this.sweepSectors.push({ id: Date.now(), path: this.currentSweepPath });
    },
    clearSectors() { this.sweepSectors = []; },
    animate() {
       if (this.running) {
          const dist = Math.sqrt(Math.pow(this.planetX - this.focusX, 2) + Math.pow(this.planetY - 140, 2));
          // Kepler speed: dAngle = dt * h / r^2
          this.angle += 350 / (dist * dist);
       }
       this.timer = requestAnimationFrame(this.animate);
    }
  },
  mounted() {
    this.animate();
  },
  beforeDestroy() {
    if(this.timer) cancelAnimationFrame(this.timer);
  }
}
</script>

<style scoped lang="scss">
.kepler-laws-combined-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fefce8; color: #a16207; border: 1px solid #fef08a; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 280px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } &.green { border-left: 3px solid #10b981; } } }
.law-tag { position: absolute; bottom: 12px; right: 12px; background: rgba(56, 189, 248, 0.1); color: #38bdf8; padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: bold; border: 1px solid rgba(56, 189, 248, 0.3); }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.flex { display: flex; } .items-center { align-items: center; } .justify-center { justify-content: center; }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 10px; i { color: #f59e0b; font-size: 1.1rem; margin-top: 2px; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
